import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Props for {@link AuthErrorBoundary}.
 */
export interface AuthErrorBoundaryProps {
  /**
   * Rendered when the wrapped auth subtree throws. Receives the caught error
   * and a `reset` callback that clears the boundary and remounts the wrapped
   * subtree — suitable for transient render errors. Note: `reset` does NOT
   * recover a Keycloak *init* failure, because a keycloak-js instance can only
   * be initialized once; recover from a sign-in outage with a full page reload.
   */
  fallback: (error: Error, reset: () => void) => ReactNode;
  /** Optional hook for reporting the caught error to a logger/monitor. */
  onError?: (error: Error, info: ErrorInfo) => void;
  children: ReactNode;
}

interface AuthErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches synchronous render errors from the auth subtree — most importantly the
 * `initError` thrown by {@link AuthProvider} when Keycloak is unreachable — so a
 * Keycloak outage degrades to a fallback screen instead of an unmounted white
 * page. AuthProvider deliberately sits above the router, so the router's own
 * error boundary cannot catch its init failure; this boundary is meant to wrap
 * AuthProvider directly.
 */
export class AuthErrorBoundary extends Component<
  AuthErrorBoundaryProps,
  AuthErrorBoundaryState
> {
  constructor(props: AuthErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError(error: Error): AuthErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  private reset(): void {
    this.setState({ error: null });
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error) {
      return this.props.fallback(error, this.reset);
    }
    return this.props.children;
  }
}

export default AuthErrorBoundary;
